type Listener = (playing: boolean) => void;

class AudioManager {
    private audio: HTMLAudioElement | null = null;
    private objectUrl: string | null = null;
    private currentListener: Listener | null = null;

    async playBlob(blob: Blob, onPlayingChange?: Listener): Promise<void> {
        // Stop any previous audio
        this.stop();

        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        this.audio = audio;
        this.objectUrl = url;
        this.currentListener = onPlayingChange ?? null;

        const setFalse = () => {
            if (this.currentListener) this.currentListener(false);
        };
        const cleanup = () => {
            audio.removeEventListener('ended', onEnded);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('error', onError);

            if (this.objectUrl) {
                URL.revokeObjectURL(this.objectUrl);
                this.objectUrl = null;
            }
        };

        const onEnded = () => {
            setFalse();
            cleanup();
        };
        const onPause = () => {
            if (this.audio === audio) setFalse();
        };
        const onError = () => {
            setFalse();
            cleanup();
        };

        audio.addEventListener('ended', onEnded);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('error', onError);

        if (this.currentListener) this.currentListener(true);

        try {
            await audio.play();
        } catch (e) {
            setFalse();
            cleanup();
            if (this.audio === audio) this.audio = null;
            throw e;
        }
    }

    stop(): void {
        if (this.audio) {
            const oldAudio = this.audio;
            const oldListener = this.currentListener;

            try {
                oldAudio.pause();
                oldAudio.currentTime = 0;
            } catch {
                throw new Error('Failed to stop audio playback.');
            }

            if (this.objectUrl) {
                URL.revokeObjectURL(this.objectUrl);
                this.objectUrl = null;
            }

            if (oldListener) oldListener(false);
        }

        this.audio = null;
        this.currentListener = null;
    }

    isPlaying(): boolean {
        return !!this.audio && !this.audio.paused;
    }
}

export const audioManager = new AudioManager();
